import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Tenant, Property, TenantStatus } from '../types/properties';
import { Users, Search, Filter, Plus, Mail, Phone, Calendar, DollarSign, Building, Trash2, Edit2, X, ShieldAlert } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Modal } from '../components/Modal';
import { useStore } from '../store/useStore';
import { formatCurrency } from '../lib/format';

export default function Tenants() {
  const { currentUser } = useStore();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);

  // Form states
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [emergencyContact, setEmergencyContact] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [monthlyRent, setMonthlyRent] = useState('');
  const [securityDeposit, setSecurityDeposit] = useState('');
  const [leaseStart, setLeaseStart] = useState('');
  const [leaseEnd, setLeaseEnd] = useState('');
  const [status, setStatus] = useState<TenantStatus>('active');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [{ data: tenantsData, error: tenantsError }, { data: propertiesData, error: propsError }] = await Promise.all([
        supabase.from('tenants').select('*').order('created_at', { ascending: false }),
        supabase.from('properties').select('*')
      ]);

      if (tenantsError) throw tenantsError;
      if (propsError) throw propsError;

      setTenants(tenantsData || []);
      setProperties(propertiesData || []);
    } catch (error) {
      console.error('Error fetching tenants data:', error);
      toast.error('Failed to load tenants data.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setEditingTenant(null);
    setName('');
    setEmail('');
    setPhone('');
    setEmergencyContact('');
    setPropertyId(properties[0]?.id || '');
    setMonthlyRent('');
    setSecurityDeposit('');
    setLeaseStart('');
    setLeaseEnd('');
    setStatus('active');
    setShowAddModal(true);
  };

  const handleOpenEdit = (tenant: Tenant) => {
    setEditingTenant(tenant);
    setName(tenant.name || '');
    setEmail(tenant.email || '');
    setPhone(tenant.phone || '');
    setEmergencyContact(tenant.emergency_contact || '');
    setPropertyId(tenant.property_id || '');
    setMonthlyRent(tenant.monthly_rent?.toString() || '');
    setSecurityDeposit(tenant.security_deposit?.toString() || '');
    setLeaseStart(tenant.lease_start || '');
    setLeaseEnd(tenant.lease_end || '');
    setStatus(tenant.status || 'active');
    setShowAddModal(true);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!propertyId) {
      toast.error('Property assignment is required');
      return;
    }

    setIsSubmitting(true);
    const tenantPayload = {
      name,
      email: email || null,
      phone: phone || null,
      emergency_contact: emergencyContact || null,
      property_id: propertyId,
      monthly_rent: monthlyRent ? parseFloat(monthlyRent) : null,
      security_deposit: securityDeposit ? parseFloat(securityDeposit) : null,
      lease_start: leaseStart || null,
      lease_end: leaseEnd || null,
      status
    };

    try {
      if (editingTenant) {
        const { error } = await supabase
          .from('tenants')
          .update(tenantPayload)
          .eq('id', editingTenant.id);
        if (error) throw error;
        toast.success('Tenant profile updated successfully');
      } else {
        const { error } = await supabase
          .from('tenants')
          .insert([tenantPayload]);
        if (error) throw error;
        toast.success('Tenant added successfully');
      }
      setShowAddModal(false);
      fetchData();
    } catch (error) {
      console.error('Save tenant error:', error);
      toast.error('Failed to save tenant. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to remove this tenant? This action cannot be undone.')) return;
    try {
      const { error } = await supabase.from('tenants').delete().eq('id', id);
      if (error) throw error;
      toast.success('Tenant deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Delete tenant error:', error);
      toast.error('Failed to delete tenant.');
    }
  };

  // Filters
  const filteredTenants = tenants.filter(t => {
    const property = properties.find(p => p.id === t.property_id);
    const searchStr = `${t.name} ${t.email || ''} ${t.phone || ''} ${property?.address || ''}`.toLowerCase();
    const matchesSearch = searchStr.includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="flex flex-col h-[calc(100vh-5rem)]">
      {/* Header Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between px-8 py-6 bg-[var(--t-surface)] border-b border-[var(--t-border)] shrink-0 gap-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-[var(--t-primary-dim)] flex items-center justify-center">
            <Users className="text-[var(--t-primary)]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[var(--t-text)] m-0">Tenant Directory</h1>
            <p className="text-sm text-[var(--t-text-muted)] mt-1">Manage tenant leases, status, and contact details</p>
          </div>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)]" size={16} />
            <input
              type="text"
              placeholder="Search tenants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl text-sm text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)] w-64"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)]" size={16} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-10 pr-8 py-2 bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl text-sm text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)] appearance-none"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="former">Former</option>
            </select>
          </div>

          <button
            onClick={handleOpenAdd}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--t-primary)] text-white text-sm font-semibold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors shadow-lg shadow-[var(--t-primary-dim)]"
          >
            <Plus size={16} /> Add Tenant
          </button>
        </div>
      </div>

      {/* Main Table Panel */}
      <div className="flex-1 overflow-auto p-8 bg-[var(--t-bg)]">
        {isLoading ? (
          <div className="text-[var(--t-text-muted)]">Loading tenants...</div>
        ) : filteredTenants.length === 0 ? (
          <div className="text-center p-12 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl max-w-4xl mx-auto">
            <Users size={48} className="mx-auto text-[var(--t-border-hover)] mb-4 animate-pulse" />
            <h3 className="text-lg font-bold text-white mb-2">No Tenants Found</h3>
            <p className="text-[var(--t-text-muted)] mb-6">Create your first tenant record to link leases and collect rent.</p>
            <button onClick={handleOpenAdd} className="px-6 py-2 bg-[var(--t-primary)] text-white font-semibold rounded-xl hover:bg-[var(--t-primary-hover)]">
              Add Tenant
            </button>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-[var(--t-surface-hover)] border-b border-[var(--t-border)]">
                  <tr>
                    <th className="px-6 py-4 font-semibold text-[var(--t-text-muted)]">Tenant Name</th>
                    <th className="px-6 py-4 font-semibold text-[var(--t-text-muted)]">Property Assigned</th>
                    <th className="px-6 py-4 font-semibold text-[var(--t-text-muted)]">Lease Terms</th>
                    <th className="px-6 py-4 font-semibold text-[var(--t-text-muted)]">Rent / Deposit</th>
                    <th className="px-6 py-4 font-semibold text-[var(--t-text-muted)]">Status</th>
                    <th className="px-6 py-4 font-semibold text-[var(--t-text-muted)] text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--t-border-subtle)]">
                  {filteredTenants.map(tenant => {
                    const property = properties.find(p => p.id === tenant.property_id);
                    return (
                      <tr key={tenant.id} className="hover:bg-[var(--t-surface-hover)]/30 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-bold text-white">{tenant.name}</div>
                          <div className="flex items-center gap-4 text-xs text-[var(--t-text-muted)] mt-1.5">
                            {tenant.email && (
                              <span className="flex items-center gap-1">
                                <Mail size={12} /> {tenant.email}
                              </span>
                            )}
                            {tenant.phone && (
                              <span className="flex items-center gap-1">
                                <Phone size={12} /> {tenant.phone}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-[var(--t-text)] font-semibold">
                            <Building size={14} className="text-[var(--t-primary)] shrink-0" />
                            <span className="truncate max-w-[220px]">{property?.address || 'Unassigned'}</span>
                          </div>
                          <span className="text-[10px] text-[var(--t-text-muted)] mt-0.5 block">
                            {property ? `${property.city}, ${property.state}` : ''}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1 text-[var(--t-text)] font-medium">
                            <Calendar size={14} className="text-[var(--t-text-muted)]" />
                            <span>
                              {tenant.lease_start ? new Date(tenant.lease_start).toLocaleDateString() : 'N/A'} - 
                              {tenant.lease_end ? new Date(tenant.lease_end).toLocaleDateString() : 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-semibold text-white">
                            {formatCurrency(tenant.monthly_rent || 0)}/mo
                          </div>
                          <div className="text-[10px] text-[var(--t-text-muted)] mt-0.5">
                            Deposit: {formatCurrency(tenant.security_deposit || 0)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`capitalize text-xs font-bold px-2.5 py-1 rounded-lg border ${
                            tenant.status === 'active' ? 'bg-[var(--t-success-dim)] text-[var(--t-success)] border-[var(--t-success)]/20' :
                            tenant.status === 'pending' ? 'bg-[var(--t-warning-dim)] text-[var(--t-warning)] border-[var(--t-warning)]/20' :
                            'bg-[var(--t-surface-hover)] text-[var(--t-text-muted)] border-[var(--t-border)]'
                          }`}>
                            {tenant.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleOpenEdit(tenant)}
                              className="p-1.5 rounded-lg border border-[var(--t-border)] hover:border-[var(--t-primary)] text-[var(--t-text-muted)] hover:text-white transition-colors"
                              title="Edit Profile"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleDelete(tenant.id)}
                              className="p-1.5 rounded-lg border border-[var(--t-border)] hover:border-[var(--t-error)] text-[var(--t-text-muted)] hover:text-[var(--t-error)] transition-colors"
                              title="Delete Tenant"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit Tenant Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title={editingTenant ? "Edit Tenant Profile" : "Add New Tenant"} maxWidth="max-w-2xl">
        <form onSubmit={handleFormSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-black uppercase text-[var(--t-text-muted)] tracking-widest block mb-1">
                Full Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-3 text-sm outline-none focus:border-[var(--t-primary)] text-[var(--t-text)] font-semibold"
                placeholder="John Doe"
                required
              />
            </div>

            <div>
              <label className="text-[10px] font-black uppercase text-[var(--t-text-muted)] tracking-widest block mb-1">
                Property Assignment *
              </label>
              <select
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
                className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-3 text-sm outline-none focus:border-[var(--t-primary)] text-[var(--t-text)]"
                required
              >
                <option value="" disabled>-- Select Property --</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.address}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-[10px] font-black uppercase text-[var(--t-text-muted)] tracking-widest block mb-1">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-3 text-sm outline-none focus:border-[var(--t-primary)] text-[var(--t-text)]"
                placeholder="john.doe@example.com"
              />
            </div>

            <div>
              <label className="text-[10px] font-black uppercase text-[var(--t-text-muted)] tracking-widest block mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-3 text-sm outline-none focus:border-[var(--t-primary)] text-[var(--t-text)]"
                placeholder="(512) 555-0199"
              />
            </div>

            <div>
              <label className="text-[10px] font-black uppercase text-[var(--t-text-muted)] tracking-widest block mb-1">
                Emergency Contact
              </label>
              <input
                type="text"
                value={emergencyContact}
                onChange={(e) => setEmergencyContact(e.target.value)}
                className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-3 text-sm outline-none focus:border-[var(--t-primary)] text-[var(--t-text)]"
                placeholder="Spouse, parent, relative..."
              />
            </div>

            <div>
              <label className="text-[10px] font-black uppercase text-[var(--t-text-muted)] tracking-widest block mb-1">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as TenantStatus)}
                className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-3 text-sm outline-none focus:border-[var(--t-primary)] text-[var(--t-text)]"
              >
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="former">Former</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] font-black uppercase text-[var(--t-text-muted)] tracking-widest block mb-1">
                Monthly Rent ($)
              </label>
              <input
                type="number"
                value={monthlyRent}
                onChange={(e) => setMonthlyRent(e.target.value)}
                className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-3 text-sm outline-none focus:border-[var(--t-primary)] text-[var(--t-text)]"
                placeholder="1500"
              />
            </div>

            <div>
              <label className="text-[10px] font-black uppercase text-[var(--t-text-muted)] tracking-widest block mb-1">
                Security Deposit ($)
              </label>
              <input
                type="number"
                value={securityDeposit}
                onChange={(e) => setSecurityDeposit(e.target.value)}
                className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-3 text-sm outline-none focus:border-[var(--t-primary)] text-[var(--t-text)]"
                placeholder="1500"
              />
            </div>

            <div>
              <label className="text-[10px] font-black uppercase text-[var(--t-text-muted)] tracking-widest block mb-1">
                Lease Start Date
              </label>
              <input
                type="date"
                value={leaseStart}
                onChange={(e) => setLeaseStart(e.target.value)}
                className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-3 text-sm outline-none focus:border-[var(--t-primary)] text-[var(--t-text)]"
              />
            </div>

            <div>
              <label className="text-[10px] font-black uppercase text-[var(--t-text-muted)] tracking-widest block mb-1">
                Lease End Date
              </label>
              <input
                type="date"
                value={leaseEnd}
                onChange={(e) => setLeaseEnd(e.target.value)}
                className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl px-4 py-3 text-sm outline-none focus:border-[var(--t-primary)] text-[var(--t-text)]"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-[var(--t-border)]">
            <button
              type="button"
              onClick={() => setShowAddModal(false)}
              className="px-6 py-2.5 rounded-xl border border-[var(--t-border)] text-sm font-semibold text-[var(--t-text-muted)] hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2.5 rounded-xl bg-[var(--t-primary)] text-white text-sm font-semibold hover:bg-[var(--t-primary-hover)] transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save Tenant'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
